import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '../components/ui/table';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import { FileText, Plus, X, Loader2, RefreshCw, ExternalLink, Package, ShoppingCart } from 'lucide-react';
import DashboardLayout from '../components/DashboardLayout';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PendingBOMPage = () => {
  const navigate = useNavigate();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [counts, setCounts] = useState({ total: 0, so_line_items: 0, wo_materials: 0 });
  const [sourceFilter, setSourceFilter] = useState('all'); // 'all', 'so_line_item', 'wo_material'

  const getAuthHeader = () => {
    const token = localStorage.getItem('token');
    return { Authorization: `Bearer ${token}` };
  };

  const fetchEntries = useCallback(async () => {
    try {
      setLoading(true);
      const sourceParam = sourceFilter !== 'all' ? `?source=${sourceFilter}` : '';
      const [entriesRes, countsRes] = await Promise.all([
        axios.get(`${API_URL}/pending-bom${sourceParam}`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/pending-bom/count`, { headers: getAuthHeader() })
      ]);
      setEntries(entriesRes.data);
      setCounts(countsRes.data);
    } catch (error) {
      toast.error('Failed to load pending BOM entries');
    } finally {
      setLoading(false);
    }
  }, [sourceFilter]);

  useEffect(() => {
    fetchEntries();
  }, [fetchEntries]);

  // Navigate to main BOM page with pre-selected item
  const handleCreateBOM = (entry) => {
    const params = new URLSearchParams({
      create: 'true',
      item_id: entry.item_id,
      item_name: entry.item_name,
      // Only include pending_bom_id for SO entries (not for dynamic WO material entries)
      ...(entry.source === 'so_line_item' && entry.id && !entry.id.startsWith('wo-mat-') && {
        pending_bom_id: entry.id,
        sales_order_id: entry.sales_order_id || '',
        so_number: entry.so_number || '',
        client_name: entry.client_name || ''
      }),
      quantity: entry.quantity?.toString() || '',
      unit: entry.unit || ''
    });
    navigate(`/production/bom?${params.toString()}`);
  };

  const handleCancelEntry = async (entryId) => {
    // Only allow cancel for SO entries (not WO material entries)
    if (entryId.startsWith('wo-mat-')) {
      toast.error('Cannot cancel WO material entries. Create a BOM or remove the material from the WO.');
      return;
    }
    
    if (!window.confirm('Are you sure you want to cancel this entry?')) return;
    
    try {
      await axios.delete(`${API_URL}/pending-bom/${entryId}`, { headers: getAuthHeader() });
      toast.success('Entry cancelled');
      fetchEntries();
    } catch (error) {
      toast.error('Failed to cancel entry');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-IN', {
      day: '2-digit', month: 'short', year: 'numeric'
    });
  };

  return (
    <DashboardLayout>
      <div className="p-6 space-y-4" data-testid="pending-bom-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Pending BOM Register</h1>
            <p className="text-sm text-gray-500 mt-1">
              Items awaiting BOM creation before Work Order can be generated
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Button variant="outline" size="sm" onClick={fetchEntries}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </div>
        </div>

        {/* Source Filter Tabs and Counts */}
        <div className="flex items-center gap-4">
          <div className="flex gap-1 bg-slate-100 p-1 rounded-lg">
            <Button 
              variant={sourceFilter === 'all' ? 'default' : 'ghost'} 
              size="sm"
              onClick={() => setSourceFilter('all')}
              className="h-8"
            >
              All ({counts.total || 0})
            </Button>
            <Button 
              variant={sourceFilter === 'so_line_item' ? 'default' : 'ghost'} 
              size="sm"
              onClick={() => setSourceFilter('so_line_item')}
              className="h-8"
            >
              <ShoppingCart className="w-3 h-3 mr-1" />
              SO Items ({counts.so_line_items || 0})
            </Button>
            <Button 
              variant={sourceFilter === 'wo_material' ? 'default' : 'ghost'} 
              size="sm"
              onClick={() => setSourceFilter('wo_material')}
              className="h-8"
            >
              <Package className="w-3 h-3 mr-1" />
              WO Materials ({counts.wo_materials || 0})
            </Button>
          </div>
        </div>

        {/* Table */}
        <Card>
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
              </div>
            ) : entries.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <FileText className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                <p>No pending BOM entries</p>
                <p className="text-sm">All items have BOMs created</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[100px]">Source</TableHead>
                    <TableHead>Reference</TableHead>
                    <TableHead>Item Name</TableHead>
                    <TableHead className="text-right">Qty</TableHead>
                    <TableHead>Delivery Date</TableHead>
                    <TableHead>Parent/Client</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {entries.map((entry) => (
                    <TableRow key={entry.id} data-testid={`pending-bom-row-${entry.id}`}>
                      {/* Source Badge */}
                      <TableCell>
                        {entry.source === 'wo_material' ? (
                          <Badge variant="secondary" className="bg-purple-100 text-purple-700">
                            <Package className="w-3 h-3 mr-1" />
                            WO
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="bg-blue-50 text-blue-700">
                            <ShoppingCart className="w-3 h-3 mr-1" />
                            SO
                          </Badge>
                        )}
                      </TableCell>
                      
                      {/* Reference */}
                      <TableCell className="font-medium">
                        {entry.source === 'wo_material' ? (
                          <span className="text-purple-600">
                            {entry.parent_wos?.length > 1 
                              ? `${entry.parent_wos[0]} +${entry.parent_wos.length - 1}` 
                              : entry.parent_wo_number}
                          </span>
                        ) : (
                          <a 
                            href={`/production/so-view/${entry.sales_order_id}`}
                            className="text-blue-600 hover:underline flex items-center gap-1"
                          >
                            {entry.item_code || entry.so_number}
                            <ExternalLink className="w-3 h-3" />
                          </a>
                        )}
                      </TableCell>
                      
                      {/* Item Name */}
                      <TableCell className="font-medium">
                        <div>
                          {entry.item_name}
                          {entry.category && (
                            <Badge variant="outline" className="ml-2 text-xs">
                              {entry.category}
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                      
                      {/* Quantity */}
                      <TableCell className="text-right">{entry.quantity} {entry.unit}</TableCell>
                      
                      {/* Delivery Date */}
                      <TableCell>{formatDate(entry.delivery_date)}</TableCell>
                      
                      {/* Parent/Client */}
                      <TableCell className="max-w-[200px] truncate">
                        {entry.source === 'wo_material' 
                          ? entry.parent_item_name 
                          : entry.client_name || '-'}
                      </TableCell>
                      
                      {/* Actions */}
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Button 
                            size="sm" 
                            onClick={() => handleCreateBOM(entry)}
                            className="bg-green-600 hover:bg-green-700"
                            data-testid={`create-bom-btn-${entry.id}`}
                          >
                            <Plus className="w-4 h-4 mr-1" />
                            Create BOM
                          </Button>
                          {entry.source === 'so_line_item' && (
                            <Button 
                              size="sm" 
                              variant="ghost"
                              onClick={() => handleCancelEntry(entry.id)}
                              className="text-red-600 hover:text-red-700"
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default PendingBOMPage;
